import { type ReactNode, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../hooks/useAuth.js";
import { useTheme } from "../hooks/useTheme.js";
import { usePermissions } from "../hooks/usePermissions.js";
import { trpc } from "../lib/trpc.js";
import {
  LayoutDashboard,
  Globe,
  BrainCircuit,
  Bot,
  GitBranch,
  Bell,
  LogOut,
  Menu,
  X,
  Sparkles,
  Cpu,
  Database,
  Activity,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Building2,
  Shield,
  MessageSquare,
  ShieldCheck,
  ScrollText,
  Scale,
  Users,
  ChevronsUpDown,
  Crown,
  Plug,
  Wrench,
} from "lucide-react";
import { useState, useEffect } from "react";

interface NavItem {
  label: string;
  href: string;
  icon: typeof LayoutDashboard;
  badge?: number;
  /** If set, item is only visible when this returns true */
  visible?: boolean;
}

export function DashboardLayout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const permissions = usePermissions();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("ym-sidebar-collapsed") === "true";
    }
    return false;
  });
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("ym-sidebar-collapsed", String(collapsed));
  }, [collapsed]);

  const { data: unreadData } = trpc.notifications.unreadCount.useQuery(undefined, {
    refetchInterval: 30_000,
    retry: false,
  });
  const unreadCount = unreadData?.count || 0;

  // Super admin: fetch all organizations for switcher
  const { data: orgsData } = trpc.admin.listOrganizations.useQuery(undefined, {
    enabled: permissions.isSuperAdmin,
    staleTime: 60_000,
  });

  const mainNav: NavItem[] = useMemo(() => [
    { label: "Dashboard", href: "/", icon: LayoutDashboard, visible: true },
    { label: "Sites", href: "/sites", icon: Globe, visible: true },
    { label: "Brain Office", href: "/brain", icon: BrainCircuit, visible: true },
    { label: "Agentes", href: "/agents", icon: Bot, visible: true },
    { label: "Pipelines", href: "/pipelines", icon: GitBranch, visible: permissions.canManagePipelines },
    { label: "Channels", href: "/channels", icon: MessageSquare, visible: permissions.canViewChannels },
    { label: "Logs", href: "/logs", icon: ScrollText, visible: permissions.canViewLogs },
  ], [permissions]);

  const infraNav: NavItem[] = useMemo(() => [
    { label: "Organização", href: "/organization", icon: Building2, visible: true },
    { label: "Integrações", href: "/integrations", icon: Plug, visible: permissions.canManageIntegrations },
    { label: "Skills", href: "/skills", icon: Wrench, visible: permissions.canManageSkills },
    { label: "Governança", href: "/governance", icon: Scale, visible: permissions.canViewGovernance },
    { label: "Vault", href: "/vault", icon: Shield, visible: permissions.canViewVault },
    { label: "LLM Gateway", href: "/llm", icon: Cpu, visible: permissions.canViewLLM },
    { label: "Memory", href: "/memory", icon: Database, visible: permissions.canViewMemory },
    { label: "System", href: "/system", icon: Activity, visible: permissions.canViewSystem },
    { label: "Security", href: "/security", icon: ShieldCheck, visible: permissions.canViewSecurity },

  ], [permissions]);

  const bottomNav: NavItem[] = useMemo(() => [
    { label: "Notificações", href: "/notifications", icon: Bell, badge: unreadCount, visible: true },
  ], [unreadCount]);

  // Filter nav items by visibility
  const visibleMainNav = mainNav.filter((item) => item.visible !== false);
  const visibleInfraNav = infraNav.filter((item) => item.visible !== false);
  const visibleBottomNav = bottomNav.filter((item) => item.visible !== false);

  const userInitial =
    user?.name?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    "?";

  const switchOrganization = trpc.admin.switchOrganization.useMutation({
    onSuccess: (data) => {
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);
      setOrgSwitcherOpen(false);
      // Reload to refresh all queries with new org context
      window.location.reload();
    },
  });

  const renderNavItem = (item: NavItem) => {
    const isActive =
      location === item.href ||
      (item.href !== "/" && location.startsWith(item.href));
    return (
      <Link key={item.href} href={item.href}>
        <div
          className={`relative flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer group ${
            collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
          } ${
            isActive
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary"
          }`}
          onClick={() => setMobileOpen(false)}
          title={collapsed ? item.label : undefined}
        >
          {isActive && !collapsed && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r-full" />
          )}
          {isActive && collapsed && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-0 w-5 h-0.5 bg-primary rounded-t-full" />
          )}
          <item.icon
            className={`w-4 h-4 shrink-0 transition-transform duration-150 ${
              isActive ? "text-primary" : "group-hover:scale-110"
            }`}
          />
          {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
          {!collapsed && item.badge != null && item.badge > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-primary text-primary-foreground text-[9px] font-bold rounded-full">
              {item.badge > 99 ? "99+" : item.badge}
            </span>
          )}
          {collapsed && item.badge != null && item.badge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full" />
          )}
        </div>
      </Link>
    );
  };

  const sidebarWidth = collapsed ? "w-16" : "w-60";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Org Switcher Overlay */}
      {orgSwitcherOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]" onClick={() => setOrgSwitcherOpen(false)}>
          <div
            className="absolute top-20 left-4 lg:left-64 w-80 max-h-96 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 border-b border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Trocar Organização</p>
            </div>
            <div className="overflow-y-auto max-h-72">
              {orgsData?.map((org) => (
                <button
                  key={org.id}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary transition-colors ${
                    org.id === user?.organizationId ? "bg-primary/10" : ""
                  }`}
                  onClick={() => switchOrganization.mutate({ organizationId: org.id })}
                  disabled={switchOrganization.isPending}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                    {org.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{org.name}</p>
                    <p className="text-[10px] text-muted-foreground">{org.memberCount} membros · {org.plan}</p>
                  </div>
                  {org.id === user?.organizationId && (
                    <span className="text-[10px] text-primary font-semibold">ATUAL</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 ${sidebarWidth} bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out ${
          mobileOpen ? "translate-x-0 !w-60" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-border shrink-0 ${collapsed ? "justify-center px-2" : "px-5"}`}>
          {!collapsed || mobileOpen ? (
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg brand-gradient flex items-center justify-center shadow-lg shadow-primary/30 shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-bold text-foreground tracking-tight leading-none">
                  <span className="text-primary">YOS</span>
                </h1>
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.2em] mt-0.5">
                  Megan AI
                </p>
              </div>
            </div>
          ) : (
            <div className="w-8 h-8 rounded-lg brand-gradient flex items-center justify-center shadow-lg shadow-primary/30">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
          )}
          <button
            className="ml-2 lg:hidden text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setMobileOpen(false)}
            aria-label="Fechar menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Super Admin Org Switcher */}
        {permissions.isSuperAdmin && (!collapsed || mobileOpen) && (
          <div className="px-2 pt-2">
            <button
              onClick={() => setOrgSwitcherOpen(true)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors group"
            >
              <Crown className="w-3.5 h-3.5 text-primary shrink-0" />
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[10px] font-semibold text-primary uppercase tracking-wider leading-none">Admin YOS</p>
                <p className="text-[10px] text-muted-foreground truncate mt-0.5">{user?.organizationName || "Global"}</p>
              </div>
              <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </button>
          </div>
        )}
        {permissions.isSuperAdmin && collapsed && !mobileOpen && (
          <div className="px-2 pt-2 flex justify-center">
            <button
              onClick={() => setOrgSwitcherOpen(true)}
              className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center hover:bg-primary/20 transition-colors"
              title="Trocar Organização (Admin YOS)"
            >
              <Crown className="w-3.5 h-3.5 text-primary" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          <div className="space-y-0.5">
            {visibleMainNav.map(renderNavItem)}
          </div>

          {visibleInfraNav.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border/50">
              {!collapsed && (
                <p className="px-3 mb-2 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-widest">
                  Infraestrutura
                </p>
              )}
              <div className="space-y-0.5">
                {visibleInfraNav.map(renderNavItem)}
              </div>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-border/50">
            <div className="space-y-0.5">
              {visibleBottomNav.map(renderNavItem)}
            </div>
          </div>
        </nav>

        {/* Theme toggle + Collapse toggle */}
        <div className={`px-2 py-2 border-t border-border/50 flex ${collapsed ? "flex-col items-center gap-1" : "items-center gap-1"}`}>
          <button
            onClick={toggleTheme}
            className={`flex items-center gap-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all ${
              collapsed ? "p-2 justify-center" : "px-3 py-2 flex-1"
            }`}
            title={theme === "dark" ? "Modo claro" : "Modo escuro"}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {!collapsed && <span className="text-xs">{theme === "dark" ? "Claro" : "Escuro"}</span>}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
            title={collapsed ? "Expandir menu" : "Recolher menu"}
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        {/* User section */}
        <div className="p-3 border-t border-border shrink-0">
          {collapsed && !mobileOpen ? (
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-8 h-8 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center text-primary text-xs font-bold cursor-pointer"
                title={user?.name || user?.email || "Usuário"}
              >
                {userInitial}
              </div>
              <button
                onClick={logout}
                className="text-muted-foreground hover:text-destructive transition-colors"
                title="Sair"
                aria-label="Sair"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-secondary/70 transition-colors group">
              <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                {userInitial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-semibold text-foreground truncate leading-tight">
                    {user?.name || user?.email}
                  </p>
                  {permissions.isSuperAdmin && (
                    <Crown className="w-3 h-3 text-primary shrink-0" />
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground truncate leading-tight mt-0.5">
                  {permissions.profileLabel} · {user?.organizationName || "YOS"}
                </p>
              </div>
              <button
                onClick={logout}
                className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                title="Sair"
                aria-label="Sair"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="h-14 flex items-center px-4 border-b border-border lg:hidden bg-card/80 backdrop-blur-sm sticky top-0 z-30">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Abrir menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="ml-3 flex items-center gap-2">
            <div className="w-6 h-6 rounded-md brand-gradient flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-semibold text-foreground text-sm">
              <span className="text-primary">YOS</span> Megan AI
            </span>
          </div>
          <div className="ml-auto">
            <button
              onClick={toggleTheme}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              title={theme === "dark" ? "Modo claro" : "Modo escuro"}
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
